import { useEffect, useState } from 'react';
import { api } from '@/lib/axios';
import {
  FeldStatus,
  feldKlasse,
  mitStand,
  useFeld,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth.store';
import {
  aiApi,
  type TenantAiConfig,
  type AiProviderInfo,
  type AiUsage,
  type AiProvider,
  type AiTaskModel,
} from '../api/ai.api';
import { KiArbeitet } from '@/components/ki/KiArbeitet';
import { AssistentEinstellungen } from '../components/AssistentEinstellungen';
import {
  pageTitle,
  formCard,
  cardTitle,
  fieldLabel,
  inputBase,
} from '@/lib/form-styles';
import {
  Bot,
  Key,
  BarChart3,
  Save,
  Trash2,
  Play,
  Loader2,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

/**
 * AP-36 Phase D: Anbieter und Modell speichern sofort (Auswahl). Der
 * API-Schlüssel bleibt eine Handlung mit eigenem Knopf (Zugangsdaten ändern).
 *
 * AP-43: Das Modell hat als erste Option „Vorgabe von Symphenia" (`model: null`) —
 * dann wählt der Betreiber je Aufgabe. Der Wechsel des Anbieters setzt das Modell
 * als Folgefeld auf diese Vorgabe zurück, statt wie zuvor eines fest einzutragen.
 * Darunter steht nur lesend, welches Modell je Aufgabe arbeitet.
 */
export default function AiSettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasModule } = useAuthStore();

  // ── State ────────────────────────────────────────────────────────────

  const [config, setConfig] = useState<TenantAiConfig | null>(null);
  const [providers, setProviders] = useState<AiProviderInfo[]>([]);
  const [usage, setUsage] = useState<AiUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: 'aiConfig',
    bezeichnung: t('ai.settings.title'),
    daten: config as unknown as Datensatz | null,
    schreibe: (patch, stand) => api.patch<Datensatz>('/ai/config', patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: '/ai/config', method: 'PATCH' },
  });
  const stellenDaten = useStellenDaten(stelle as Speicherstelle<unknown>);
  const selectedProvider = ((stellenDaten.provider as AiProvider | undefined) ?? config?.provider ?? 'ANTHROPIC') as AiProvider;

  // API-Key form
  const [newKey, setNewKey] = useState('');
  const [showKeyInput, setShowKeyInput] = useState(false);

  // Saving / Feedback
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Test
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  // Delete confirmation
  const [confirmDelete, setConfirmDelete] = useState(false);

  // ── Module-Guard ─────────────────────────────────────────────────────

  useEffect(() => {
    if (!hasModule('AI_ASSISTANT')) {
      navigate('/settings/modules', { replace: true });
    }
  }, [hasModule, navigate]);

  // ── Data loading ─────────────────────────────────────────────────────

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO(AP-08): Abhaengigkeiten pruefen; Ergaenzen kann zusaetzliche Laeufe ausloesen
  }, []);

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      const [cfgRes, provRes, usageRes] = await Promise.all([
        aiApi.getConfig(),
        aiApi.getProviders(),
        aiApi.getUsage(),
      ]);
      setConfig(cfgRes.data);
      setProviders(provRes.data);
      setUsage(usageRes.data);
    } catch {
      setError(t('ai.errors.loadError'));
    } finally {
      setLoading(false);
    }
  }

  // ── Derived data ─────────────────────────────────────────────────────


  // ── Handlers ─────────────────────────────────────────────────────────

  async function handleSaveKey() {
    if (!newKey.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await aiApi.updateConfig({ apiKey: newKey.trim() });
      setConfig(res.data);
      setNewKey('');
      setShowKeyInput(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      // Usage refreshen weil BYOK-Status sich ändert
      const usageRes = await aiApi.getUsage();
      setUsage(usageRes.data);
    } catch (err: any) {
      setSaveError(err.response?.data?.message ?? t('ai.errors.keyInvalid'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteKey() {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await aiApi.deleteApiKey();
      setConfig(res.data);
      setConfirmDelete(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      const usageRes = await aiApi.getUsage();
      setUsage(usageRes.data);
    } catch (err: any) {
      setSaveError(err.response?.data?.message ?? t('ai.settings.saveError'));
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    setTestError(null);
    try {
      const res = await aiApi.test();
      setTestResult(
        t('ai.test.success', { content: res.data.content.slice(0, 200) }),
      );
      // Refresh usage after test
      const usageRes = await aiApi.getUsage();
      setUsage(usageRes.data);
    } catch (err: any) {
      setTestError(
        t('ai.test.failed', {
          reason: err.response?.data?.message ?? err.message,
        }),
      );
    } finally {
      setTesting(false);
    }
  }

  // ── Render ───────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  const usagePercent = usage
    ? usage.limit > 0
      ? Math.min(usage.percentageUsed, 100)
      : 0
    : 0;

  return (
    <div className="mx-auto max-w-4xl p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <Bot size={24} className="text-primary" />
          <h1 className={pageTitle}>{t('ai.settings.title')}</h1>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('ai.settings.subtitle')}
        </p>
      </div>

      {/* Feedback */}
      {saved && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400">
          <CheckCircle size={16} />
          {t('ai.settings.saved')}
        </div>
      )}
      {saveError && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          <AlertCircle size={16} />
          {saveError}
        </div>
      )}

      <div className="space-y-6">
        {/* ── AP-18: Assistent — an/aus, Schlüsselquelle, Einwilligung ──── */}
        <AssistentEinstellungen />

        {/* ── Karte 1: Provider & Modell ────────────────────────────────── */}
        <div className={formCard}>
          <div className="mb-4 flex items-center gap-2">
            <Bot size={18} className="text-primary" />
            <h2 className={cardTitle}>{t('ai.settings.providerCard')}</h2>
          </div>

          <AnbieterUndModell stelle={stelle as Speicherstelle<unknown>} providers={providers} />
          <AufgabenModelle zeilen={(stellenDaten.taskModels as AiTaskModel[] | undefined) ?? config?.taskModels ?? []} />
        </div>

        {/* ── Karte 2: API-Key (BYOK) ──────────────────────────────────── */}
        <div className={formCard}>
          <div className="mb-4 flex items-center gap-2">
            <Key size={18} className="text-primary" />
            <h2 className={cardTitle}>
              {t('ai.settings.keyCard')} — {selectedProvider}
            </h2>
          </div>
          <p className="mb-3 text-xs text-muted-foreground">
            {t('ai.settings.keyProviderHint', { provider: selectedProvider })}
          </p>

          {config?.hasApiKey && !showKeyInput ? (
            /* Key ist hinterlegt */
            <div>
              <div className="mb-3 flex items-center gap-2">
                <CheckCircle size={16} className="text-emerald-500" />
                <span className="text-sm font-medium text-foreground">
                  {t('ai.settings.ownKeyActive')} ({t('ai.settings.keySuffix', { suffix: config.apiKeySuffix })})
                </span>
              </div>
              <p className="mb-4 text-xs text-muted-foreground">
                {t('ai.settings.byokHint')}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowKeyInput(true)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-primary/30 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
                >
                  <Key size={14} />
                  {t('ai.settings.changeKey')}
                </button>
                {!confirmDelete ? (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-destructive/30 px-3 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
                  >
                    <Trash2 size={14} />
                    {t('ai.settings.deleteKey')}
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {t('ai.settings.deleteKeyConfirm')}
                    </span>
                    <button
                      onClick={handleDeleteKey}
                      disabled={saving}
                      className="rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-destructive-foreground transition-colors hover:bg-destructive/90 disabled:opacity-50"
                    >
                      {t('common.delete')}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Kein Key oder "Neuen Key eingeben" */
            <div>
              {!config?.hasApiKey && (
                <p className="mb-3 text-xs text-muted-foreground">
                  {(config?.monthlyLimit ?? 100000) > 0
                    ? t('ai.settings.centralKeyHint', {
                        limit: (config?.monthlyLimit ?? 100000).toLocaleString('de-DE'),
                      })
                    : t('ai.settings.noCentralQuota')}
                </p>
              )}
              <div className="space-y-1.5">
                <label className={fieldLabel}>
                  {t('ai.settings.enterKey')}
                </label>
                <div className="flex gap-2">
                  <input
                    type="password"
                    className={inputBase}
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    placeholder={t('ai.settings.enterKeyPlaceholder')}
                  />
                  <button
                    onClick={handleSaveKey}
                    disabled={saving || !newKey.trim()}
                    className="inline-flex flex-shrink-0 items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                  >
                    {saving ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Save size={16} />
                    )}
                    {t('ai.settings.storeKey')}
                  </button>
                </div>
              </div>
              {showKeyInput && config?.hasApiKey && (
                <button
                  onClick={() => {
                    setShowKeyInput(false);
                    setNewKey('');
                  }}
                  className="mt-2 text-xs text-muted-foreground underline transition-colors hover:text-foreground"
                >
                  {t('common.cancel')}
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Karte 3: Nutzung & Limit ─────────────────────────────────── */}
        <div className={formCard}>
          <div className="mb-4 flex items-center gap-2">
            <BarChart3 size={18} className="text-primary" />
            <h2 className={cardTitle}>{t('ai.settings.usageCard')}</h2>
          </div>

          {usage && (
            <div className="space-y-4">
              {/* Nutzungsanzeige */}
              <div>
                <p className="text-sm font-medium text-foreground">
                  {t('ai.settings.usageThisMonth')} ({usage.period})
                </p>

                <div className="mt-2 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-md border border-border/50 bg-background p-3">
                    <p className="text-xs text-muted-foreground">{t('ai.settings.tokens')}</p>
                    <p className="text-lg font-semibold text-foreground">
                      {usage.totalTokens.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {usage.inputTokens.toLocaleString()} in / {usage.outputTokens.toLocaleString()} out
                    </p>
                  </div>
                  <div className="rounded-md border border-border/50 bg-background p-3">
                    <p className="text-xs text-muted-foreground">{t('ai.settings.requests')}</p>
                    <p className="text-lg font-semibold text-foreground">
                      {usage.requests.toLocaleString()}
                    </p>
                  </div>
                  <div className="rounded-md border border-border/50 bg-background p-3">
                    <p className="text-xs text-muted-foreground">{t('ai.settings.limit')}</p>
                    <p className="text-lg font-semibold text-foreground">
                      {usage.byok
                        ? t('ai.settings.unlimited')
                        : usage.limit > 0
                          ? usage.limit.toLocaleString('de-DE')
                          : t('ai.settings.noQuota')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Progress-Bar (nur bei zentralem Key + Limit > 0) */}
              {!usage.byok && usage.limit > 0 && (
                <div>
                  <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{usagePercent.toFixed(1)}%</span>
                    <span>
                      {usage.totalTokens.toLocaleString()} / {usage.limit.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full rounded-full transition-all ${
                        usagePercent >= 90
                          ? 'bg-destructive'
                          : usagePercent >= 70
                            ? 'bg-amber-500'
                            : 'bg-primary'
                      }`}
                      style={{ width: `${usagePercent}%` }}
                    />
                  </div>
                </div>
              )}

              {usage.byok && (
                <p className="text-xs text-muted-foreground">
                  {t('ai.settings.byokNoLimit')}
                </p>
              )}

              {/* Test-Button */}
              <div className="border-t border-border/50 pt-4">
                <button
                  onClick={handleTest}
                  disabled={testing}
                  className="inline-flex items-center gap-2 rounded-md border border-primary/30 px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/10 disabled:opacity-50"
                >
                  <Play size={16} />
                  {testing ? <KiArbeitet text={t('ai.test.running')} /> : t('ai.test.button')}
                </button>

                {testResult && (
                  <div className="mt-3 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400">
                    {testResult}
                  </div>
                )}
                {testError && (
                  <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
                    {testError}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Anbieter und Modell — Auswahl speichert sofort; ein neuer Anbieter beginnt mit der Vorgabe von Symphenia. */
function AnbieterUndModell({ stelle, providers }: { stelle: Speicherstelle<unknown>; providers: AiProviderInfo[] }) {
  const { t } = useTranslation();
  const anbieter = useFeld<AiProvider>(stelle, 'provider', {
    art: 'auswahl',
    label: t('ai.settings.provider'),
    pflicht: true,
    lies: (x) => x as AiProvider,
    // AP-43: Das Modell des alten Anbieters gilt beim neuen nicht — zurück auf die Vorgabe, im selben PATCH.
    folge: () => ({ model: null }),
  });
  const modell = useFeld(stelle, 'model', { art: 'auswahl', label: t('ai.settings.model') });
  const modelle = providers.find((p) => p.provider === anbieter.wert)?.supportedModels ?? [];

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-1.5">
        <label className={fieldLabel}>{t('ai.settings.provider')}</label>
        <select className={feldKlasse} {...anbieter.props}>
          {providers.map((p) => (
            <option key={p.provider} value={p.provider}>
              {p.provider}
            </option>
          ))}
        </select>
        <FeldStatus status={anbieter} />
      </div>
      <div className="space-y-1.5">
        <label className={fieldLabel}>{t('ai.settings.model')}</label>
        <select className={feldKlasse} {...modell.props}>
          <option value="">{t('ai.settings.modelDefault')}</option>
          {modelle.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <FeldStatus status={modell} />
      </div>
    </div>
  );
}

/** AP-43 (7.): „Welches Modell arbeitet für dich" — nur lesend, je Aufgabe der gebuchten Bausteine. */
function AufgabenModelle({ zeilen }: { zeilen: AiTaskModel[] }) {
  const { t } = useTranslation();
  if (!zeilen.length) return null;
  return (
    <div className="mt-6 border-t border-border/50 pt-4">
      <h3 className="text-sm font-semibold text-foreground">{t('ai.settings.taskModelsTitle')}</h3>
      <p className="mt-1 text-xs text-muted-foreground">{t('ai.settings.taskModelsHint')}</p>
      <table className="mt-3 w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="py-1 pr-4 font-semibold">{t('ai.settings.taskModelsTask')}</th>
            <th className="py-1 pr-4 font-semibold">{t('ai.settings.taskModelsModel')}</th>
            <th className="py-1 font-semibold">{t('ai.settings.taskModelsSource')}</th>
          </tr>
        </thead>
        <tbody>
          {zeilen.map((z) => (
            <tr key={z.task} className="border-t border-border/40">
              <td className="py-1.5 pr-4 text-foreground">{z.label}</td>
              <td className="py-1.5 pr-4 font-mono text-xs text-foreground">{z.model}</td>
              <td className="py-1.5 text-muted-foreground">
                {z.source === 'TENANT' ? t('ai.settings.sourceTenant') : t('ai.settings.sourceOperator')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
